import React, { Fragment, useState } from 'react';
import { useTranslate, Button } from 'react-admin';
import {
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  SvgIcon,
  CircularProgress,
} from '@material-ui/core';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { useMutation } from 'react-query';

import { useHandbook, useNotify } from '~/hooks';
import { EditButton } from '~/components';
import { eventLogUpdate } from '~/utils';
import { beginKycCheck } from '~/api/kyc';
import { Business, Individual } from '~/types';
import { ShieldCheckmarkIcon } from '~/img';
import { getProfileTypeByResource } from '~/utils/profileType';

const label = (resource: string, key: string): string => `resources.${resource}.fields.${key}`;

interface Props {
  resource: string;
  record: Individual | Business | undefined;
  solutionId?: string;
  permissions: string[];
}

const KYCLevelCheck = (props: Props) => {
  const { solutionId, resource, record, permissions } = props;
  const notify = useNotify();
  const translate = useTranslate();
  const t = (key: string): string => translate(label(resource, key));

  const currentKYCLevel = record?.kycLevel;
  const [selectedKYCLevel, setSelectedKYCLevel] = useState<string>(currentKYCLevel || '');

  const { data } = useHandbook(solutionId);
  const kycLevels = (data?.kycLevels || []).sort((a, b) => {
    return a.priority - b.priority;
  });

  const { mutateAsync: beginKycCheckAsync, isLoading } = useMutation(beginKycCheck);

  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    if (currentKYCLevel) {
      setSelectedKYCLevel(currentKYCLevel);
    }
    setOpen(false);
  };

  const profileType = getProfileTypeByResource(resource);
  const handleCheckKyc = () => {
    if (
      !record ||
      !selectedKYCLevel ||
      (profileType !== 'INDIVIDUAL' && profileType !== 'BUSINESS')
    )
      return null;

    return beginKycCheckAsync({
      profileType,
      profileId: record.id,
      targetKycLevel: selectedKYCLevel,
    }).then(() => {
      notify({ message: t('checkKYCInitiated'), type: 'info' });
      eventLogUpdate(resource);
      handleClose();
    });
  };

  const classes = useStyles();

  return (
    <Fragment>
      <EditButton
        label={t('checkKYC')}
        icon={
          <SvgIcon>
            <ShieldCheckmarkIcon />
          </SvgIcon>
        }
        onClick={handleOpen}
        disabled={!permissions.includes('kyc.workflow.start')}
      />
      <Dialog onClose={handleClose} aria-labelledby='simple-dialog-title' open={open}>
        <DialogTitle className={classes.DialogTitle} aria-label='simple-dialog-title'>
          {t('selectKYCtoCheck')}
        </DialogTitle>
        <DialogContent>
          <Grid container justify='center'>
            {kycLevels?.map((item, index) => {
              return (
                <Grid item xs={12} key={item.id}>
                  <MenuItem
                    component='button'
                    selected={selectedKYCLevel === item.id}
                    onClick={() => setSelectedKYCLevel(item.id)}
                    key={index}
                    value={item.id}
                    className={classes.menuItem}
                    disabled={currentKYCLevel === item.id}
                  >
                    {item.name}
                  </MenuItem>
                </Grid>
              );
            })}
          </Grid>
          <Grid container spacing={1} justify='center' className={classes.topSpacing}>
            <Grid item>
              <Button label='ra.action.cancel' onClick={handleClose} />
            </Grid>
            <Grid item>
              <Button
                label={t('checkKYC')}
                variant='contained'
                onClick={handleCheckKyc}
                disabled={selectedKYCLevel === currentKYCLevel || !selectedKYCLevel || isLoading}
              >
                {isLoading ? (
                  <CircularProgress size={18} />
                ) : (
                  <SvgIcon>
                    <ShieldCheckmarkIcon />
                  </SvgIcon>
                )}
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </Fragment>
  );
};

const useStyles = makeStyles((theme) => ({
  menuItem: {
    width: '100%',
    justifyContent: 'center',
  },
  topSpacing: {
    marginTop: theme.spacing(2),
  },
  DialogTitle: {
    '& > h2': {
      display: 'flex',
      justifyContent: 'center',
    },
  },
}));

export default KYCLevelCheck;
